import React from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  configApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { MarkdownContent } from '@backstage/core-components';
import useAsync from 'react-use/esm/useAsync';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import { Autocomplete } from '@material-ui/lab';
import { CircularProgress, TextField, makeStyles } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';

interface TeamOption {
  slug: string;
  role: string;
  onboarded?: boolean;
}

const useStyles = makeStyles(() => ({
  maintainerOption: {
    fontWeight: 600,
  },
  onboardedOption: {
    opacity: 0.8,
    fontStyle: 'italic',
  },
  memberOption: {
    opacity: 0.6,
  },
}));

export const GithubTeamPicker = ({
  onChange,
  required,
  rawErrors,
  formData,
  schema: { title = 'GitHub Team', description },
}: FieldExtensionComponentProps<string>) => {
  const classes = useStyles();
  const config = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);

  const backendUrl = config.getString('backend.baseUrl');

  const {
    value: teams,
    loading,
    error,
  } = useAsync(async (): Promise<TeamOption[]> => {
    const res = await fetchApi.fetch(
      `${backendUrl}/api/teams-picker/teams-for-onboarding`,
      {
        method: 'GET',
        headers: { Accept: 'application/json' },
      },
    );

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Failed to fetch teams: ${res.status} ${text}`);
    }

    return res.json();
  }, []);

  if (error) {
    return (
      <FormControl margin="normal" required={required} fullWidth>
        <Alert severity="error">
          Failed to load teams: {error.message}
        </Alert>
      </FormControl>
    );
  }

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0}
      fullWidth
    >
      <Autocomplete
        loading={loading}
        options={(teams ?? []).slice().sort((a, b) => {
          const order = (t: TeamOption) =>
            t.role === 'MAINTAINER' && !t.onboarded ? 0 : t.onboarded ? 1 : 2;
          return order(a) - order(b);
        })}
        getOptionLabel={(option: TeamOption) => option.slug}
        getOptionDisabled={(option: TeamOption) =>
          option.role !== 'MAINTAINER' || !!option.onboarded
        }
        groupBy={(option: TeamOption) => {
          if (option.role === 'MAINTAINER' && !option.onboarded)
            return 'Teams you can onboard';
          if (option.onboarded) return 'Already onboarded';
          return 'Teams where you are member';
        }}
        value={teams?.find(t => t.slug === formData) ?? null}
        onChange={(_, newValue) => {
          onChange(newValue?.slug || undefined);
        }}
        renderOption={(option: TeamOption) => (
          <span
            className={
              option.onboarded
                ? classes.onboardedOption
                : option.role === 'MAINTAINER'
                  ? classes.maintainerOption
                  : classes.memberOption
            }
          >
            {option.slug}
          </span>
        )}
        renderInput={params => (
          <TextField
            {...params}
            label={title}
            variant="outlined"
            required={required}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {description && (
        <FormHelperText component="div">
          <MarkdownContent content={description} />
        </FormHelperText>
      )}
      {rawErrors?.length > 0 && (
        <FormHelperText error>{rawErrors.join(', ')}</FormHelperText>
      )}
    </FormControl>
  );
};
